/**
 * Offline Journal - Append-only log of project saves pending cloud sync.
 *
 * v1 IMPLEMENTATION NOTE:
 *   This implementation stores full project snapshots for deterministic replay.
 *   This is correct and simple but does not scale to very large projects with
 *   high save frequency. Future v2 path: delta/mutation-aware journal with
 *   periodic full checkpoints:
 *     { type: "checkpoint", sequenceNum, fullSnapshot }
 *     { type: "delta", sequenceNum, mutations: FieldMutation[] }
 *
 * STORAGE CAPS (v1):
 *   - MAX_ENTRIES: 3 full snapshots per project journal
 *   - MAX_BYTES:   2 MB per project journal
 *   - Overflow eviction: prefer oldest synced → then oldest unsynced (logged as error)
 *
 * CRASH RECOVERY:
 *   On boot, getAllUnsynced() returns all pending entries across all projects.
 *   The bridge re-enqueues them for cloud sync.
 *
 * Storage key: nuvra-journal-{projectId}
 */

import { logger } from "@/system/logger";

const JOURNAL_PREFIX = "nuvra-journal-";
const MAX_ENTRIES    = 3;
const MAX_BYTES      = 2_000_000; // 2 MB cap per project journal

export interface JournalEntry {
  entryId:        string;       // unique ID for this entry
  projectId:      string;
  sequenceNum:    number;
  schemaVersion:  number;
  snapshot:       unknown;      // full ProjectContentPayload at time of save
  savedAt:        number;       // client epoch ms
  syncedAt:       number | null; // null until cloud confirms
  attempts:       number;
  lastError:      string | null;
  estimatedBytes: number;       // approximate size in bytes
}

type Journal = JournalEntry[];

/* =========================================================
   HELPERS
   ========================================================= */

function key(projectId: string): string {
  return `${JOURNAL_PREFIX}${projectId}`;
}

function read(projectId: string): Journal {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(key(projectId));
    return raw ? (JSON.parse(raw) as Journal) : [];
  } catch {
    return [];
  }
}

function write(projectId: string, journal: Journal): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key(projectId), JSON.stringify(journal));
  } catch (err) {
    logger.error("OfflineJournal write failed", { projectId, err });
  }
}

function estimateBytes(payload: unknown): number {
  try { return JSON.stringify(payload).length * 2; } catch { return 50_000; }
}

function evictOne(journal: Journal, projectId: string): Journal {
  const syncedIdx = journal.findIndex(e => e.syncedAt !== null);
  if (syncedIdx >= 0) {
    const evicted = journal.splice(syncedIdx, 1)[0];
    logger.debug("OfflineJournal evicted synced entry", { projectId, seq: evicted.sequenceNum });
  } else {
    const evicted = journal.shift();
    logger.error("OfflineJournal evicted UNSYNCED entry due to cap", {
      projectId,
      droppedSeq: evicted?.sequenceNum,
    });
  }
  return journal;
}

/* =========================================================
   OFFLINE JOURNAL API
   ========================================================= */

export const OfflineJournal = {

  append(entry: Omit<JournalEntry, "estimatedBytes">): void {
    let journal = read(entry.projectId);
    const bytes = estimateBytes(entry.snapshot);

    // Check total byte cap
    const totalBytes = journal.reduce((s, e) => s + e.estimatedBytes, 0) + bytes;
    if (totalBytes > MAX_BYTES) {
      logger.warn("OfflineJournal byte cap triggered - evicting", {
        projectId: entry.projectId,
        totalBytes,
      });
      journal = evictOne(journal, entry.projectId);
    }

    // Check entry count cap
    while (journal.length >= MAX_ENTRIES) {
      journal = evictOne(journal, entry.projectId);
    }

    journal.push({ ...entry, estimatedBytes: bytes });
    write(entry.projectId, journal);
  },

  markSynced(projectId: string, sequenceNum: number): void {
    const journal = read(projectId);
    const entry   = journal.find(e => e.sequenceNum === sequenceNum);
    if (entry) {
      entry.syncedAt = Date.now();
      write(projectId, journal);
    }
  },

  recordAttempt(projectId: string, sequenceNum: number, error: string | null): void {
    const journal = read(projectId);
    const entry   = journal.find(e => e.sequenceNum === sequenceNum);
    if (entry) {
      entry.attempts  += 1;
      entry.lastError  = error;
      write(projectId, journal);
    }
  },

  getUnsynced(projectId: string): JournalEntry[] {
    return read(projectId)
      .filter(e => e.syncedAt === null)
      .sort((a, b) => a.sequenceNum - b.sequenceNum);
  },

  /** Used on app boot for crash recovery - scans all journal keys. */
  getAllUnsynced(): JournalEntry[] {
    if (typeof localStorage === "undefined") return [];
    const projectIds: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(JOURNAL_PREFIX)) {
        projectIds.push(k.slice(JOURNAL_PREFIX.length));
      }
    }
    return projectIds
      .flatMap(id => OfflineJournal.getUnsynced(id))
      .sort((a, b) => a.savedAt - b.savedAt);
  },

  getLatest(projectId: string): JournalEntry | null {
    const journal = read(projectId);
    if (!journal.length) return null;
    return journal.reduce((best, e) =>
      e.sequenceNum > best.sequenceNum ? e : best
    );
  },

  /** Housekeeping: remove synced entries older than retentionMs. */
  prune(projectId: string, retentionMs = 86_400_000): void {
    const now     = Date.now();
    const journal = read(projectId).filter(
      e => e.syncedAt === null || now - e.syncedAt < retentionMs
    );
    write(projectId, journal);
  },

  estimatedBytes(projectId: string): number {
    return read(projectId).reduce((s, e) => s + e.estimatedBytes, 0);
  },
};
